import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import store from '../state/store';

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// --- HELPER DE REDIRECTION SELON LE RÔLE ---
function getHomeRouteForUser(user) {
  if (!user || !user.type) return { name: 'default' };

  const roleName = user.type.nom;

  switch (roleName) {
    case 'Facturation':
      return { name: 'listeCommandes' };
    case 'Caissier':
    case 'Stock':
      return { name: 'listeFactures' };
    case 'POS':
      return { path: '/ventes/gui_pos' };
    default:
      return { name: 'default' }; // Dashboard par défaut (ex: Admin / Super Admin)
  }
}

// --- GARDE DE NAVIGATION UNIQUE ET FUSIONNÉE ---
router.beforeEach((routeTo, routeFrom, next) => {
  const publicPages = ['/login', '/register', '/forgot-password', '/landing', '/logout'];
  const isPublicPage = publicPages.includes(routeTo.path) || routeTo.path.startsWith('/register/step2');
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired) || !isPublicPage;

  const currentUser = store.state.currentUser;
  const loggedUser = localStorage.getItem('currentUser');

  // 1. Utilisateur NON CONNECTÉ essayant d'accéder à une page protégée
  if (authRequired && (!currentUser || !loggedUser || currentUser.enabled === false)) {
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  // 2. Utilisateur CONNECTÉ essayant d'aller sur une page publique (ex: Login)
  if (isPublicPage && currentUser) {
    return next(getHomeRouteForUser(currentUser));
  }

  // Si la page est publique et non connecté, on laisse passer
  if (isPublicPage) {
    return next();
  }

  // 3. Gestion de la racine '/' ou du Dashboard par défaut
  // Si l'utilisateur clique sur la racine ou le dashboard général, on l'oriente vers son dashboard spécifique
  // ✅ FIXED (Section 3)
  if (routeTo.path === '/' || routeTo.name === 'default') {
    switch (store.state.currentUser?.type?.nom) {
      case 'Facturation':
        return next({ name: 'listeCommandes' });
      case 'Caissier':
      case 'Stock':
        return next({ name: 'listeFactures' });
      case 'POS':
        return next({ path: '/ventes/gui_pos' });
      default:
        return next();
    }
  }

  // 4. Restriction stricte pour le rôle POS
  if (currentUser?.type?.nom === 'POS' && routeTo.path !== '/ventes/gui_pos') {
    return next({ path: '/ventes/gui_pos' });
  }

  // 5. Vérification des PERMISSIONS par rôle (RBAC)
  if (authRequired) {
    const isSuperAdmin = currentUser?.type?.nom === 'Super Admin';
    const hasPermission = currentUser?.type?.permissions?.some(p => p === routeTo.meta.key);

    // Si pas de meta.key définie sur la route, ou si Super Admin, ou si l'utilisateur a la permission
    if (!routeTo.meta.key || isSuperAdmin || hasPermission) {
      return next();
    } else {
      // Accès refusé
      return next({ name: 'Unauthorized' });
    }
  }

  next();
});

// --- RESOLVE TITLE & METAS ---
router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    }
  } catch (error) {
    return;
  }

  if (routeTo.meta && routeTo.meta.title) {
    document.title = routeTo.meta.title + ' | ' + appConfig.title;
  }
  next();
});

export default router;